import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

export interface AppModalProps {
    open?: boolean;
    title?: React.ReactNode;
    children?: React.ReactNode;
    onCancel?: () => void;
    onClose?: () => void;
    onOk?: () => void;
    okText?: React.ReactNode;
    cancelText?: React.ReactNode;
    okButtonProps?: { danger?: boolean } & Record<string, any>;
    cancelButtonProps?: Record<string, any>;
    confirmLoading?: boolean;
    footer?: React.ReactNode;
    width?: number | string;
    centered?: boolean;
    destroyOnClose?: boolean;
    maskClosable?: boolean;
    closable?: boolean;
    className?: string;
    style?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
}

const AppModal = ({
    open = false,
    title,
    children,
    onCancel,
    onClose,
    onOk,
    okText = "OK",
    cancelText = "Cancel",
    okButtonProps,
    cancelButtonProps,
    confirmLoading,
    footer,
    width,
    maskClosable = true,
    closable = true,
    className,
    style,
    bodyStyle,
}: AppModalProps) => {
    const dismiss = onCancel || onClose || (() => undefined);

    const { danger, ...restOkProps } = okButtonProps || {};

    const hasCustomFooter = footer !== undefined;
    const emptyFooter =
        footer === null || (Array.isArray(footer) && footer.length === 0);

    return (
        <Dialog
            open={open}
            onClose={(_e, reason) => {
                if (reason === "backdropClick" && maskClosable === false) return;
                dismiss();
            }}
            className={className}
            style={style}
            maxWidth={width ? 'lg' : 'sm'}
            fullWidth
            PaperProps={width ? { sx: { width, maxWidth: width } } : undefined}
        >
            {title != null && (
                <DialogTitle sx={{ pr: closable !== false ? 6 : undefined }}>
                    {title}
                    {closable !== false && (
                        <IconButton
                            aria-label="close"
                            onClick={dismiss}
                            size="medium"
                            sx={{ position: "absolute", right: 8, top: 8 }}
                        >
                            <CloseIcon fontSize="medium" />
                        </IconButton>
                    )}
                </DialogTitle>
            )}

            <DialogContent style={bodyStyle}>{children}</DialogContent>

            {hasCustomFooter
                ? !emptyFooter && <DialogActions>{footer}</DialogActions>
                : (
                    <DialogActions>
                        <Button onClick={dismiss} {...(cancelButtonProps || {})}>
                            {cancelText}
                        </Button>
                        <Button
                            onClick={onOk}
                            variant="contained"
                            color={danger ? "error" : "primary"}
                            style={{ boxShadow: "none", backgroundColor: danger ? "#df2427" : "#1976d2" }}
                            disabled={confirmLoading}
                            {...restOkProps}
                        >
                            {okText}
                        </Button>
                    </DialogActions>
                )}
        </Dialog>
    );
};

export default AppModal;
